<template>
  <div>
    <div class="page-header">
      <div class="page-icon">🖨️</div>
      <h2>打印设置</h2>
    </div>

    <el-row :gutter="20">
      <!-- 左：表单 -->
      <el-col :xs="24" :md="12">
        <el-card class="box-card">
          <el-form :model="form" label-width="100px">
            <el-form-item label="店名">
              <el-input v-model="form.storeName" placeholder="小票顶部店名" maxlength="20" show-word-limit></el-input>
            </el-form-item>

            <el-form-item label="销售单标题">
              <el-input v-model="form.saleLabel" placeholder="默认：销售单（可改：出货单）" maxlength="10"></el-input>
            </el-form-item>

            <el-form-item label="进货单标题">
              <el-input v-model="form.purchaseLabel" placeholder="默认：进货单（可改：收货单）" maxlength="10"></el-input>
            </el-form-item>

            <el-form-item label="页脚">
              <el-input v-model="form.footer" placeholder="默认：谢谢惠顾" maxlength="30"></el-input>
            </el-form-item>

            <el-form-item label="纸张宽度">
              <el-radio-group v-model="form.paperWidth">
                <el-radio :value="58">58 mm</el-radio>
                <el-radio :value="80">80 mm</el-radio>
              </el-radio-group>
            </el-form-item>

            <!-- 底部品牌广告区 -->
            <el-divider content-position="left">底部品牌广告（茶意荟）</el-divider>

            <el-form-item label="启用广告">
              <el-switch v-model="form.promo.enabled" active-text="在小票底部显示品牌宣传与微信二维码" inline-prompt></el-switch>
            </el-form-item>

            <el-form-item label="品牌名">
              <el-input v-model="form.promo.brand" placeholder="默认：茶意荟" maxlength="12" :disabled="!form.promo.enabled"></el-input>
            </el-form-item>

            <el-form-item label="广告文案">
              <div style="width: 100%;">
                <el-input
                  v-for="(line, idx) in form.promo.lines"
                  :key="idx"
                  v-model="form.promo.lines[idx]"
                  :placeholder="`第 ${idx + 1} 行（按字数 ≤28/58mm 32 字符自动居中）`"
                  maxlength="32"
                  clearable
                  style="margin-bottom: 6px;"
                  :disabled="!form.promo.enabled"
                >
                  <template #append>
                    <el-button :icon="Delete" @click="removePromoLine(idx)" :disabled="!form.promo.enabled"></el-button>
                  </template>
                </el-input>
                <el-button :icon="Plus" size="small" plain @click="addPromoLine" :disabled="!form.promo.enabled || form.promo.lines.length >= 6">添加一行</el-button>
              </div>
            </el-form-item>

            <el-form-item label="微信二维码">
              <el-input
                v-model="form.promo.qrText"
                placeholder="可填微信号 / 微信链接 / 任意文本（扫码后看到的内容）"
                :disabled="!form.promo.enabled"
                clearable
              ></el-input>
            </el-form-item>

            <el-form-item label="二维码说明">
              <el-input v-model="form.promo.qrCaption" placeholder="如：扫码加微信  享会员价" maxlength="32" :disabled="!form.promo.enabled"></el-input>
            </el-form-item>

            <el-form-item label="二维码大小">
              <el-slider v-model="form.promo.qrSize" :min="3" :max="10" :step="1" show-stops :marks="{ 3: '小', 6: '中', 8: '大', 10: '超大' }" :disabled="!form.promo.enabled"></el-slider>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="handleSave">保存设置</el-button>
              <el-button type="success" @click="handleTestPrint">测试打印</el-button>
              <el-button type="warning" plain @click="handleClearCache">清除已配对打印机</el-button>
            </el-form-item>
          </el-form>

          <el-alert
            v-if="!webUSPSupported"
            type="error"
            :closable="false"
            title="当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>

          <el-alert
            v-else
            type="info"
            :closable="false"
            title="首次打印会弹出浏览器授权框选择 USB 设备；Windows 用户需先用 Zadig 把驱动替换为 WinUSB 才能连接热敏打印机"
            show-icon
            style="margin-top: 16px;"
          ></el-alert>
        </el-card>
      </el-col>

      <!-- 右：实时预览 -->
      <el-col :xs="24" :md="12">
        <el-card class="box-card">
          <template #header>
            <div class="preview-header">
              <span>实时预览（样例数据）</span>
              <el-radio-group v-model="previewType" size="small">
                <el-radio-button value="sale">{{ form.saleLabel }}</el-radio-button>
                <el-radio-button value="purchase">{{ form.purchaseLabel }}</el-radio-button>
              </el-radio-group>
            </div>
          </template>

          <div class="receipt-preview" :style="{ fontSize: form.paperWidth === 80 ? '13px' : '11px' }">
            <div class="r-store">{{ preview.storeName }}</div>
            <div class="r-type">── {{ preview.typeLabel }} ──</div>

            <div class="r-section-title">商品明细</div>
            <div class="r-sep">{{ preview.sep }}</div>

            <div v-for="(it, idx) in preview.items" :key="idx">
              <div class="r-name" :title="it.nameFull">{{ it.name }}</div>
              <div class="r-line">{{ it.line }}</div>
            </div>

            <div class="r-sep">{{ preview.sep }}</div>

            <div class="r-summary">数量: {{ preview.totalQuantity }}</div>
            <div class="r-summary">总计: {{ preview.total }} 元</div>

            <div v-if="preview.date" class="r-date">{{ preview.date }}</div>
            <div class="r-footer">{{ preview.footer }}</div>

            <!-- 底部品牌广告预览 -->
            <template v-if="preview.promo">
              <div class="r-promo-sep">────────────────────────</div>
              <div class="r-promo-brand">{{ preview.promo.brand }}</div>
              <div v-for="(line, i) in preview.promo.lines" :key="'pl'+i" class="r-promo-line">{{ line }}</div>
              <div v-if="preview.promo.qrText" class="r-promo-qr">
                <FakeQr :size="10" :text="preview.promo.qrText" :caption="preview.promo.qrCaption" />
              </div>
            </template>

            <div class="r-footer">　</div>
            <div class="r-footer">　</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, h } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Delete } from '@element-plus/icons-vue';
import { receiptSettings } from '@/utils/receiptSettings';
import { isWebUSPSupported, printToThermalPrinter, clearPrinterCache } from '@/utils/thermalPrinter';
import { buildTestBytes, buildReceiptPreview } from '@/utils/escpos';

/**
 * 伪二维码组件（仅用于前端预览）
 * 用稳定的字符串哈希生成一个伪 QR 外观（不是真正可扫的 QR）
 * 真实打印走 ESC/POS GS ( k 指令，真实可扫
 */
const FakeQr = {
  props: ['size', 'text', 'caption'],
  setup(props) {
    return () => {
      const N = 21; // 21x21 模块
      const modules = [];
      // 用 text 的字符码生成稳定伪随机矩阵
      const seed = String(props.text || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
      for (let y = 0; y < N; y++) {
        for (let x = 0; x < N; x++) {
          // 三个定位框（左上/右上/左下）
          const inFinder = (cx, cy) =>
            (x >= cx && x < cx + 7 && y >= cy && y < cy + 7) &&
            ((x === cx || x === cx + 6 || y === cy || y === cy + 6) ||
             (x >= cx + 2 && x <= cx + 4 && y >= cy + 2 && y <= cy + 4));
          if (inFinder(0, 0) || inFinder(N - 7, 0) || inFinder(0, N - 7)) {
            modules.push({ x, y, dark: true });
          } else {
            // 伪随机：与位置 + seed 异或
            const v = (x * 31 + y * 17 + seed * 7) & 0xff;
            if (v < 128) modules.push({ x, y, dark: true });
          }
        }
      }
      const rects = modules.map(m =>
        h('rect', { x: m.x, y: m.y, width: 1, height: 1, fill: '#000' })
      );
      return h('div', { class: 'fake-qr' }, [
        h('svg', {
          width: props.size * 8,
          height: props.size * 8,
          viewBox: `0 0 ${N} ${N}`,
          xmlns: 'http://www.w3.org/2000/svg',
          style: 'display:inline-block;'
        }, rects),
        props.caption ? h('div', { class: 'fake-qr-cap' }, props.caption) : null
      ]);
    };
  }
};

const webUSPSupported = ref(false);
const previewType = ref('sale');

const form = ref({
  storeName: '',
  saleLabel: '销售单',
  purchaseLabel: '进货单',
  footer: '谢谢惠顾',
  paperWidth: 80,
  promo: {
    enabled: false,
    brand: '茶意荟',
    lines: [
      '好茶 · 好价 · 好服务',
      '产地直供  零售批发',
      '散茶礼盒  企业定制'
    ],
    qrText: '',
    qrCaption: '扫码加微信  享会员价',
    qrSize: 6
  }
});

const SAMPLE_ITEMS = [
  { name: '可口可乐', quantity: 2, price: 3.0, subtotal: 6.0 },
  { name: '雪碧', quantity: 1, price: 3.5, subtotal: 3.5 },
  { name: '百事可乐（特价）', quantity: 3, price: 6.5, subtotal: 19.5 }
];

const preview = computed(() => buildReceiptPreview({
  storeName: form.value.storeName || '店铺名',
  type: previewType.value,
  typeLabel: previewType.value === 'purchase' ? form.value.purchaseLabel : form.value.saleLabel,
  date: new Date().toLocaleString('zh-CN'),
  items: SAMPLE_ITEMS,
  total: SAMPLE_ITEMS.reduce((a, x) => a + x.subtotal, 0),
  totalQuantity: SAMPLE_ITEMS.reduce((a, x) => a + x.quantity, 0),
  footer: form.value.footer || '谢谢惠顾',
  promo: form.value.promo
}));

onMounted(() => {
  webUSPSupported.value = isWebUSPSupported();
  const saved = receiptSettings.getPromo();
  form.value = {
    storeName: receiptSettings.getStoreName(),
    saleLabel: receiptSettings.getSaleLabel(),
    purchaseLabel: receiptSettings.getPurchaseLabel(),
    footer: receiptSettings.getFooter(),
    paperWidth: receiptSettings.getPaperWidth(),
    promo: {
      enabled: !!saved.enabled,
      brand: saved.brand,
      lines: Array.isArray(saved.lines) && saved.lines.length ? [...saved.lines] : ['', '', ''],
      qrText: saved.qrText || '',
      qrCaption: saved.qrCaption || '',
      qrSize: Number(saved.qrSize) || 6
    }
  };
});

function addPromoLine() {
  if (form.value.promo.lines.length >= 6) return;
  form.value.promo.lines.push('');
}

function removePromoLine(idx) {
  if (form.value.promo.lines.length <= 1) return;
  form.value.promo.lines.splice(idx, 1);
}

function handleSave() {
  receiptSettings.setStoreName(form.value.storeName.trim());
  receiptSettings.setSaleLabel(form.value.saleLabel.trim());
  receiptSettings.setPurchaseLabel(form.value.purchaseLabel.trim());
  receiptSettings.setFooter(form.value.footer.trim());
  receiptSettings.setPaperWidth(form.value.paperWidth);
  receiptSettings.setPromo({
    enabled: !!form.value.promo.enabled,
    brand: (form.value.promo.brand || '').trim() || '茶意荟',
    lines: form.value.promo.lines.map(s => String(s || '').trim()).filter(Boolean),
    qrText: (form.value.promo.qrText || '').trim(),
    qrCaption: (form.value.promo.qrCaption || '').trim(),
    qrSize: Math.max(3, Math.min(10, Number(form.value.promo.qrSize) || 6))
  });
  ElMessage.success('设置已保存');
}

async function handleTestPrint() {
  if (!webUSPSupported.value) {
    ElMessage.error('当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器');
    return;
  }
  try {
    const bytes = buildTestBytes();
    const dev = await printToThermalPrinter(bytes);
    ElMessage.success('已发送到打印机: ' + (dev.productName || '热敏打印机'));
  } catch (e) {
    if (e.name === 'NotFoundError') return;
    console.error('[测试打印] 失败:', e);
    ElMessage.error('测试打印失败：' + e.message);
  }
}

async function handleClearCache() {
  try {
    await ElMessageBox.confirm(
      '清除后下次打印需要重新选择 USB 设备，确定吗？',
      '提示',
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
    );
    clearPrinterCache();
    ElMessage.success('已清除，下次打印会重新弹出授权框');
  } catch (e) {
    /* 用户取消 */
  }
}
</script>

<style scoped>
.box-card {
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  border: 1px solid var(--border-lighter);
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.receipt-preview {
  font-family: 'Consolas', 'Courier New', monospace;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  padding: 16px 12px;
  color: #333;
  line-height: 1.5;
  white-space: nowrap;
  overflow-x: auto;
}
.r-store { text-align: center; font-size: 1.5em; font-weight: 700; margin-bottom: 4px; }
.r-type { text-align: center; margin-bottom: 12px; }
.r-section-title { text-align: center; margin: 4px 0 2px; }
.r-sep { color: #909399; margin: 2px 0; }
.r-summary { text-align: right; font-weight: 600; margin: 2px 0; }
.r-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.r-line { white-space: nowrap; }
.r-date, .r-footer { text-align: center; }

/* 底部品牌广告预览 */
.r-promo-sep { color: #c0c4cc; text-align: center; margin: 8px 0 4px; letter-spacing: -1px; overflow: hidden; white-space: nowrap; }
.r-promo-brand { text-align: center; font-size: 1.5em; font-weight: 800; color: #67c23a; margin: 6px 0; letter-spacing: 2px; }
.r-promo-line { text-align: center; font-size: 0.95em; color: #555; margin: 1px 0; }
.r-promo-qr { display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 8px 0 4px; }
.fake-qr { display: flex; flex-direction: column; align-items: center; }
.fake-qr svg { background: #fff; padding: 4px; border: 1px solid #eee; }
.fake-qr-cap { text-align: center; color: #666; font-size: 0.9em; margin-top: 4px; }
</style>